import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiLogIn,
  FiLogOut,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiSave,
  FiX,
  FiFileText,
  FiSettings,
  FiMail,
  FiHelpCircle,
  FiShield,
  FiFile,
  FiBarChart2,
} from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../utils/api';
import './Admin.css';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Dashboard stats
  const [stats, setStats] = useState({
    blogPosts: 0,
    contactSubmissions: { total: 0, new: 0, read: 0, replied: 0 },
    faqs: 0,
  });

  // Blog Posts
  const [blogPosts, setBlogPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showPostForm, setShowPostForm] = useState(false);
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    image: null,
    imageUrl: null,
  });

  // Contact Submissions
  const [contactSubmissions, setContactSubmissions] = useState([]);

  // FAQ
  const [faqs, setFaqs] = useState([]);
  const [showFaqForm, setShowFaqForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [faqForm, setFaqForm] = useState({
    question: '',
    answer: '',
    display_order: 0,
    is_active: true,
  });

  // Site Settings
  const [siteSettings, setSiteSettings] = useState({
    company_name: '',
    company_email: '',
    company_phone: '',
    company_address: '',
    facebook_url: '',
    twitter_url: '',
    linkedin_url: '',
    instagram_url: '',
    youtube_url: '',
    business_hours_monday: '',
    business_hours_tuesday: '',
    business_hours_wednesday: '',
    business_hours_thursday: '',
    business_hours_friday: '',
    business_hours_saturday: '',
    business_hours_sunday: '',
  });

  // Privacy Policy & Terms
  const [privacyPolicy, setPrivacyPolicy] = useState({ title: '', content: '', version: '1.0' });
  const [termsOfService, setTermsOfService] = useState({ title: '', content: '', version: '1.0' });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchDashboardData();
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      switch (activeTab) {
        case 'blog':
          fetchBlogPosts();
          break;
        case 'contact':
          fetchContactSubmissions();
          break;
        case 'faq':
          fetchFAQs();
          break;
        case 'settings':
          fetchSiteSettings();
          break;
        case 'privacy':
          fetchPrivacyPolicy();
          break;
        case 'terms':
          fetchTermsOfService();
          break;
        default:
          fetchDashboardData();
      }
    }
  }, [isAuthenticated, activeTab]);

  const fetchDashboardData = async () => {
    try {
      const [blogRes, contactCountRes, faqRes] = await Promise.all([
        api.get('/blog'),
        api.get('/contact/count'),
        api.get('/faq/admin'),
      ]);

      const posts = blogRes.data?.data || blogRes.data || [];
      const contactCount = contactCountRes.data?.data || contactCountRes.data || {};
      const faqs = faqRes.data?.data || faqRes.data || [];

      setStats({
        blogPosts: Array.isArray(posts) ? posts.length : 0,
        contactSubmissions: contactCount,
        faqs: Array.isArray(faqs) ? faqs.length : 0,
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchBlogPosts = async () => {
    try {
      const response = await api.get('/blog');
      const posts = response.data?.data || response.data || [];
      setBlogPosts(Array.isArray(posts) ? posts : []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setBlogPosts([]);
    }
  };

  const fetchContactSubmissions = async () => {
    try {
      const response = await api.get('/contact');
      const submissions = response.data?.data || response.data || [];
      setContactSubmissions(Array.isArray(submissions) ? submissions : []);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
      setContactSubmissions([]);
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await api.get('/faq/admin');
      const faqs = response.data?.data || response.data || [];
      setFaqs(Array.isArray(faqs) ? faqs : []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setFaqs([]);
    }
  };

  const fetchSiteSettings = async () => {
    try {
      const response = await api.get('/site-settings');
      const settings = response.data?.data || response.data || {};
      setSiteSettings((prev) => ({ ...prev, ...settings }));
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await api.get('/policies/admin/privacy');
      const policy = response.data?.data || response.data || [];
      if (Array.isArray(policy) && policy.length > 0) {
        setPrivacyPolicy(policy[0]);
      }
    } catch (error) {
      console.error('Error fetching privacy policy:', error);
    }
  };

  const fetchTermsOfService = async () => {
    try {
      const response = await api.get('/policies/admin/terms');
      const terms = response.data?.data || response.data || [];
      if (Array.isArray(terms) && terms.length > 0) {
        setTermsOfService(terms[0]);
      }
    } catch (error) {
      console.error('Error fetching terms of service:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const response = await api.post('/admin/login', { username, password });
      localStorage.setItem('admin_token', response.data.data?.token || response.data.token);
      setIsAuthenticated(true);
      fetchDashboardData();
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setActiveTab('dashboard');
  };

  // Blog Post Handlers
  const handleDeletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await api.delete(`/blog/${id}`);
      fetchBlogPosts();
      fetchDashboardData();
    } catch (error) {
      alert('Error deleting post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      excerpt: post.excerpt,
      content: post.content || '',
      image: null,
      imageUrl: post.image || null, // Store existing image URL
    });
    setShowPostForm(true);
    setIsEditing(true);
  };

  const handleSavePost = async (e) => {
    e.preventDefault();

    try {
      let imageUrl = postForm.imageUrl || null;

      // Upload new image if provided
      if (postForm.image) {
        const formData = new FormData();
        formData.append('image', postForm.image);

        const uploadResponse = await api.post('/upload/blog-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        imageUrl = uploadResponse.data?.data?.imageUrl || uploadResponse.data?.imageUrl;
      }

      const payload = {
        title: postForm.title,
        excerpt: postForm.excerpt,
        content: postForm.content,
        image: imageUrl,
      };

      if (isEditing && editingPost) {
        await api.put(`/blog/${editingPost.id}`, payload);
      } else {
        await api.post('/blog', payload);
      }

      setShowPostForm(false);
      setIsEditing(false);
      setEditingPost(null);
      setPostForm({ title: '', excerpt: '', content: '', image: null, imageUrl: null });
      fetchBlogPosts();
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving post:', error);
      alert(error.response?.data?.message || 'Error saving post');
    }
  };

  // Contact Submission Handlers
  const handleUpdateContactStatus = async (id, status) => {
    try {
      await api.put(`/contact/${id}/status`, { status });
      fetchContactSubmissions();
      fetchDashboardData();
    } catch (error) {
      alert('Error updating status');
    }
  };

  const handleDeleteContact = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      await api.delete(`/contact/${id}`);
      fetchContactSubmissions();
      fetchDashboardData();
    } catch (error) {
      alert('Error deleting submission');
    }
  };

  // FAQ Handlers
  const handleSaveFAQ = async (e) => {
    e.preventDefault();

    try {
      if (editingFaq) {
        await api.put(`/faq/${editingFaq.id}`, faqForm);
      } else {
        await api.post('/faq', faqForm);
      }

      setShowFaqForm(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', display_order: 0, is_active: true });
      fetchFAQs();
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving FAQ');
    }
  };

  const handleEditFAQ = (faq) => {
    setEditingFaq(faq);
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      display_order: faq.display_order,
      is_active: faq.is_active,
    });
    setShowFaqForm(true);
  };

  const handleDeleteFAQ = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await api.delete(`/faq/${id}`);
      fetchFAQs();
      fetchDashboardData();
    } catch (error) {
      alert('Error deleting FAQ');
    }
  };

  // Site Settings Handler
  const handleSaveSiteSettings = async (e) => {
    e.preventDefault();

    try {
      await api.put('/site-settings', siteSettings);
      alert('Site settings updated successfully!');
      fetchSiteSettings();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving site settings');
    }
  };

  // Privacy Policy Handler
  const handleSavePrivacyPolicy = async (e) => {
    e.preventDefault();

    try {
      await api.post('/policies/privacy', privacyPolicy);
      alert('Privacy Policy updated successfully!');
      fetchPrivacyPolicy();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving privacy policy');
    }
  };

  // Terms of Service Handler
  const handleSaveTermsOfService = async (e) => {
    e.preventDefault();

    try {
      await api.post('/policies/terms', termsOfService);
      alert('Terms of Service updated successfully!');
      fetchTermsOfService();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving terms of service');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-login page-transition">
        <motion.div
          className="admin-login-container"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="admin-login-title">Admin Login</h1>
          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" className="btn btn-primary">
              <FiLogIn /> Login
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: FiBarChart2 },
    { id: 'blog', label: 'Blog Posts', icon: FiFileText },
    { id: 'contact', label: 'Contact Submissions', icon: FiMail },
    { id: 'faq', label: 'FAQ', icon: FiHelpCircle },
    { id: 'settings', label: 'Site Settings', icon: FiSettings },
    { id: 'privacy', label: 'Privacy Policy', icon: FiShield },
    { id: 'terms', label: 'Terms of Service', icon: FiFile },
  ];

  return (
    <div className="admin page-transition">
      <div className="admin-header">
        <div className="container">
          <div className="admin-header-content">
            <h1>Admin Panel</h1>
            <button onClick={handleLogout} className="btn btn-logout">
              <FiLogOut /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="admin-content">
        <div className="container">
          <div className="admin-layout">
            <aside className="admin-sidebar">
              <nav className="admin-nav">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    className={`admin-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <tab.icon />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </aside>

            <main className="admin-main">
              {/* Dashboard Tab */}
              {activeTab === 'dashboard' && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Dashboard Overview</h2>
                  <div className="admin-stats-grid">
                    <div className="admin-stat-card">
                      <FiFileText className="admin-stat-icon" />
                      <div className="admin-stat-content">
                        <h3>{stats.blogPosts}</h3>
                        <p>Blog Posts</p>
                      </div>
                    </div>
                    <div className="admin-stat-card">
                      <FiMail className="admin-stat-icon" />
                      <div className="admin-stat-content">
                        <h3>{stats.contactSubmissions.total || 0}</h3>
                        <p>Contact Submissions</p>
                        <small>
                          {stats.contactSubmissions.new || 0} new, {stats.contactSubmissions.read || 0} read
                        </small>
                      </div>
                    </div>
                    <div className="admin-stat-card">
                      <FiHelpCircle className="admin-stat-icon" />
                      <div className="admin-stat-content">
                        <h3>{stats.faqs}</h3>
                        <p>FAQs</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Blog Posts Tab */}
              {activeTab === 'blog' && (
                <div className="admin-section">
                  <div className="admin-section-header">
                    <h2 className="admin-section-title">Blog Posts</h2>
                    <button
                      onClick={() => {
                        setShowPostForm(true);
                        setIsEditing(false);
                        setEditingPost(null);
                        setPostForm({ title: '', excerpt: '', content: '', image: null, imageUrl: null });
                      }}
                      className="btn btn-primary"
                    >
                      <FiPlus /> New Post
                    </button>
                  </div>

                  {showPostForm && (
                    <motion.div
                      className="admin-form-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="admin-form-header">
                        <h3>{isEditing ? 'Edit Post' : 'New Post'}</h3>
                        <button
                          onClick={() => {
                            setShowPostForm(false);
                            setIsEditing(false);
                            setEditingPost(null);
                          }}
                          className="btn-icon"
                        >
                          <FiX />
                        </button>
                      </div>
                      <form onSubmit={handleSavePost}>
                        <div className="form-group">
                          <label>Title</label>
                          <input
                            type="text"
                            value={postForm.title}
                            onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Excerpt</label>
                          <textarea
                            value={postForm.excerpt}
                            onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })}
                            required
                            rows="3"
                          />
                        </div>
                        <div className="form-group">
                          <label>Content</label>
                          <ReactQuill
                            theme="snow"
                            value={postForm.content}
                            onChange={(content) => setPostForm({ ...postForm, content })}
                            modules={{
                              toolbar: [
                                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                                [{ font: [] }],
                                [{ size: [] }],
                                ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                                [{ script: 'sub' }, { script: 'super' }],
                                [{ direction: 'rtl' }],
                                [{ color: [] }, { background: [] }],
                                [{ align: [] }],
                                ['link', 'image', 'video'],
                                ['clean'],
                              ],
                            }}
                            style={{ height: '400px', marginBottom: '50px' }}
                          />
                        </div>
                        <div className="form-group">
                          <label>Image</label>
                          {postForm.imageUrl && !postForm.image && (
                            <div className="admin-image-preview">
                              <img src={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${postForm.imageUrl}`} alt="Current" />
                              <button
                                type="button"
                                onClick={() => setPostForm({ ...postForm, imageUrl: null })}
                                className="btn-remove-image"
                              >
                                Remove
                              </button>
                            </div>
                          )}
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => setPostForm({ ...postForm, image: e.target.files[0], imageUrl: null })}
                          />
                          {postForm.image && (
                            <div className="admin-image-preview">
                              <p>Selected: {postForm.image.name}</p>
                            </div>
                          )}
                        </div>
                        <button type="submit" className="btn btn-primary">
                          <FiSave /> {isEditing ? 'Update' : 'Create'} Post
                        </button>
                      </form>
                    </motion.div>
                  )}

                  <div className="admin-list">
                    {Array.isArray(blogPosts) && blogPosts.length > 0 ? (
                      blogPosts.map((post) => (
                        <div key={post.id} className="admin-item">
                          <div className="admin-item-info">
                            <h3>{post.title}</h3>
                            <p>{post.excerpt}</p>
                            <span className="admin-item-date">
                              {post.published_at
                                ? new Date(post.published_at).toLocaleDateString()
                                : new Date(post.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="admin-item-actions">
                            <button onClick={() => handleEditPost(post)} className="btn-icon btn-edit">
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              className="btn-icon btn-delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="admin-empty">No blog posts yet. Create your first post!</p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Submissions Tab */}
              {activeTab === 'contact' && (
                <div className="admin-section">
                  <div className="admin-section-header">
                    <h2 className="admin-section-title">
                      Contact Submissions ({contactSubmissions.length})
                    </h2>
                  </div>

                  <div className="admin-list">
                    {Array.isArray(contactSubmissions) && contactSubmissions.length > 0 ? (
                      contactSubmissions.map((submission) => (
                        <div key={submission.id} className="admin-item">
                          <div className="admin-item-info">
                            <div className="admin-item-header">
                              <h3>{submission.name}</h3>
                              <span className={`admin-status-badge admin-status-${submission.status}`}>
                                {submission.status}
                              </span>
                            </div>
                            <p className="admin-item-email">{submission.email}</p>
                            {submission.phone && <p className="admin-item-phone">{submission.phone}</p>}
                            <p className="admin-item-subject">
                              <strong>Subject:</strong> {submission.subject}
                            </p>
                            <p className="admin-item-message">{submission.message}</p>
                            <span className="admin-item-date">
                              {new Date(submission.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div className="admin-item-actions">
                            <select
                              value={submission.status}
                              onChange={(e) => handleUpdateContactStatus(submission.id, e.target.value)}
                              className="admin-status-select"
                            >
                              <option value="new">New</option>
                              <option value="read">Read</option>
                              <option value="replied">Replied</option>
                            </select>
                            <button
                              onClick={() => handleDeleteContact(submission.id)}
                              className="btn-icon btn-delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="admin-empty">No contact submissions yet.</p>
                    )}
                  </div>
                </div>
              )}

              {/* FAQ Tab */}
              {activeTab === 'faq' && (
                <div className="admin-section">
                  <div className="admin-section-header">
                    <h2 className="admin-section-title">FAQ Management</h2>
                    <button
                      onClick={() => {
                        setShowFaqForm(true);
                        setEditingFaq(null);
                        setFaqForm({ question: '', answer: '', display_order: 0, is_active: true });
                      }}
                      className="btn btn-primary"
                    >
                      <FiPlus /> New FAQ
                    </button>
                  </div>

                  {showFaqForm && (
                    <motion.div
                      className="admin-form-card"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <div className="admin-form-header">
                        <h3>{editingFaq ? 'Edit FAQ' : 'New FAQ'}</h3>
                        <button
                          onClick={() => {
                            setShowFaqForm(false);
                            setEditingFaq(null);
                          }}
                          className="btn-icon"
                        >
                          <FiX />
                        </button>
                      </div>
                      <form onSubmit={handleSaveFAQ}>
                        <div className="form-group">
                          <label>Question</label>
                          <input
                            type="text"
                            value={faqForm.question}
                            onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Answer</label>
                          <textarea
                            value={faqForm.answer}
                            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
                            required
                            rows="5"
                          />
                        </div>
                        <div className="form-group-row">
                          <div className="form-group">
                            <label>Display Order</label>
                            <input
                              type="number"
                              value={faqForm.display_order}
                              onChange={(e) =>
                                setFaqForm({ ...faqForm, display_order: parseInt(e.target.value) || 0 })
                              }
                            />
                          </div>
                          <div className="form-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={faqForm.is_active}
                                onChange={(e) =>
                                  setFaqForm({ ...faqForm, is_active: e.target.checked })
                                }
                              />
                              Active
                            </label>
                          </div>
                        </div>
                        <button type="submit" className="btn btn-primary">
                          <FiSave /> {editingFaq ? 'Update' : 'Create'} FAQ
                        </button>
                      </form>
                    </motion.div>
                  )}

                  <div className="admin-list">
                    {Array.isArray(faqs) && faqs.length > 0 ? (
                      faqs.map((faq) => (
                        <div key={faq.id} className="admin-item">
                          <div className="admin-item-info">
                            <h3>{faq.question}</h3>
                            <p>{faq.answer}</p>
                            <div className="admin-item-meta">
                              <span>Order: {faq.display_order}</span>
                              <span className={faq.is_active ? 'admin-active' : 'admin-inactive'}>
                                {faq.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                          </div>
                          <div className="admin-item-actions">
                            <button onClick={() => handleEditFAQ(faq)} className="btn-icon btn-edit">
                              <FiEdit />
                            </button>
                            <button
                              onClick={() => handleDeleteFAQ(faq.id)}
                              className="btn-icon btn-delete"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="admin-empty">No FAQs yet. Create your first FAQ!</p>
                    )}
                  </div>
                </div>
              )}

              {/* Site Settings Tab */}
              {activeTab === 'settings' && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Site Settings</h2>
                  <form onSubmit={handleSaveSiteSettings} className="admin-form-card">
                    <div className="admin-form-section">
                      <h3>Contact Information</h3>
                      <div className="form-group">
                        <label>Company Name</label>
                        <input
                          type="text"
                          value={siteSettings.company_name}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, company_name: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={siteSettings.company_email}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, company_email: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="text"
                          value={siteSettings.company_phone}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, company_phone: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Address</label>
                        <textarea
                          value={siteSettings.company_address}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, company_address: e.target.value })
                          }
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="admin-form-section">
                      <h3>Social Media URLs</h3>
                      <div className="form-group">
                        <label>Facebook URL</label>
                        <input
                          type="url"
                          value={siteSettings.facebook_url}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, facebook_url: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Twitter URL</label>
                        <input
                          type="url"
                          value={siteSettings.twitter_url}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, twitter_url: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>LinkedIn URL</label>
                        <input
                          type="url"
                          value={siteSettings.linkedin_url}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, linkedin_url: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>Instagram URL</label>
                        <input
                          type="url"
                          value={siteSettings.instagram_url}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, instagram_url: e.target.value })
                          }
                        />
                      </div>
                      <div className="form-group">
                        <label>YouTube URL</label>
                        <input
                          type="url"
                          value={siteSettings.youtube_url}
                          onChange={(e) =>
                            setSiteSettings({ ...siteSettings, youtube_url: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="admin-form-section">
                      <h3>Business Hours</h3>
                      <div className="form-group-row">
                        <div className="form-group">
                          <label>Monday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_monday}
                            onChange={(e) =>
                              setSiteSettings({ ...siteSettings, business_hours_monday: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Tuesday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_tuesday}
                            onChange={(e) =>
                              setSiteSettings({ ...siteSettings, business_hours_tuesday: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Wednesday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_wednesday}
                            onChange={(e) =>
                              setSiteSettings({
                                ...siteSettings,
                                business_hours_wednesday: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Thursday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_thursday}
                            onChange={(e) =>
                              setSiteSettings({
                                ...siteSettings,
                                business_hours_thursday: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Friday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_friday}
                            onChange={(e) =>
                              setSiteSettings({ ...siteSettings, business_hours_friday: e.target.value })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Saturday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_saturday}
                            onChange={(e) =>
                              setSiteSettings({
                                ...siteSettings,
                                business_hours_saturday: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="form-group">
                          <label>Sunday</label>
                          <input
                            type="text"
                            value={siteSettings.business_hours_sunday}
                            onChange={(e) =>
                              setSiteSettings({ ...siteSettings, business_hours_sunday: e.target.value })
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <button type="submit" className="btn btn-primary btn-large">
                      <FiSave /> Save Settings
                    </button>
                  </form>
                </div>
              )}

              {/* Privacy Policy Tab */}
              {activeTab === 'privacy' && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Privacy Policy</h2>
                  <form onSubmit={handleSavePrivacyPolicy} className="admin-form-card">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={privacyPolicy.title}
                        onChange={(e) => setPrivacyPolicy({ ...privacyPolicy, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Version</label>
                      <input
                        type="text"
                        value={privacyPolicy.version}
                        onChange={(e) =>
                          setPrivacyPolicy({ ...privacyPolicy, version: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Content</label>
                      <ReactQuill
                        theme="snow"
                        value={privacyPolicy.content}
                        onChange={(content) =>
                          setPrivacyPolicy({ ...privacyPolicy, content })
                        }
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ align: [] }],
                            ['link'],
                            ['clean'],
                          ],
                        }}
                        style={{ height: '400px', marginBottom: '50px' }}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-large">
                      <FiSave /> Save Privacy Policy
                    </button>
                  </form>
                </div>
              )}

              {/* Terms of Service Tab */}
              {activeTab === 'terms' && (
                <div className="admin-section">
                  <h2 className="admin-section-title">Terms of Service</h2>
                  <form onSubmit={handleSaveTermsOfService} className="admin-form-card">
                    <div className="form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        value={termsOfService.title}
                        onChange={(e) =>
                          setTermsOfService({ ...termsOfService, title: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Version</label>
                      <input
                        type="text"
                        value={termsOfService.version}
                        onChange={(e) =>
                          setTermsOfService({ ...termsOfService, version: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Content</label>
                      <ReactQuill
                        theme="snow"
                        value={termsOfService.content}
                        onChange={(content) =>
                          setTermsOfService({ ...termsOfService, content })
                        }
                        modules={{
                          toolbar: [
                            [{ header: [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                            [{ align: [] }],
                            ['link'],
                            ['clean'],
                          ],
                        }}
                        style={{ height: '400px', marginBottom: '50px' }}
                      />
                    </div>
                    <button type="submit" className="btn btn-primary btn-large">
                      <FiSave /> Save Terms of Service
                    </button>
                  </form>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
